import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import {
  Loader2, Link2, Unlink, RefreshCw, ExternalLink, Check, Copy, AlertTriangle
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { integrationService } from '@/services/integrationService'
import { projectService } from '@/services/projectService'

const IntegrationSettings = ({ projectId }) => {
  const queryClient = useQueryClient()
  const [selectedProvider, setSelectedProvider] = useState('')
  const [configForm, setConfigForm] = useState({})
  const [externalProjectKey, setExternalProjectKey] = useState('')
  const [syncEnabled, setSyncEnabled] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [disconnectOpen, setDisconnectOpen] = useState(false)
  const [savingMappings, setSavingMappings] = useState(false)
  const [statusMappingForm, setStatusMappingForm] = useState({})
  const [webhookCopied, setWebhookCopied] = useState(false)

  // Fetch available providers
  const { data: providersData } = useQuery(
    'integration-providers',
    () => integrationService.getProviders()
  )

  // Fetch current integration for this project
  const { data: integrationData, isLoading } = useQuery(
    ['project-integration', projectId],
    () => integrationService.getIntegration(projectId),
    {
      onSuccess: (data) => {
        const integration = data?.data?.integration
        if (integration) {
          setSelectedProvider(integration.provider)
          setConfigForm(integration.config || {})
          setExternalProjectKey(integration.external_project_key || '')
          setSyncEnabled(integration.sync_enabled)
        }
      }
    }
  )

  // Fetch project statuses (local)
  const { data: statusData } = useQuery(
    ['projects', projectId, 'statuses'],
    () => projectService.getStatuses(projectId)
  )

  // Fetch external statuses (only when connected)
  const integration = integrationData?.data?.integration
  const { data: externalStatusData } = useQuery(
    ['project-integration-statuses', projectId],
    () => integrationService.getExternalStatuses(projectId),
    { enabled: !!integration }
  )

  // Fetch status mappings
  const { data: mappingsData } = useQuery(
    ['project-integration-mappings', projectId],
    () => integrationService.getStatusMappings(projectId),
    {
      enabled: !!integration,
      onSuccess: (data) => {
        const mappings = data?.data?.mappings || []
        const form = {}
        mappings.forEach(m => {
          form[m.project_status_id] = {
            external_status_id: m.external_status_id,
            external_status_name: m.external_status_name,
          }
        })
        setStatusMappingForm(form)
      }
    }
  )

  const providers = providersData?.data?.providers || []
  const localStatuses = statusData?.data?.statuses || []
  const externalStatuses = externalStatusData?.data?.statuses || []
  const currentProvider = providers.find(p => p.name === selectedProvider)

  const handleConnect = async () => {
    if (!selectedProvider || !externalProjectKey) {
      return toast.error('Please select a provider and enter the external project key')
    }
    setSaving(true)
    try {
      const result = await integrationService.saveIntegration(projectId, {
        provider: selectedProvider,
        config: configForm,
        external_project_key: externalProjectKey,
        sync_enabled: syncEnabled,
      })
      toast.success(result.message || 'Integration connected')
      queryClient.invalidateQueries(['project-integration', projectId])
      queryClient.invalidateQueries(['project-integration-statuses', projectId])
      queryClient.invalidateQueries(['project-integration-mappings', projectId])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to connect')
    } finally {
      setSaving(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      await integrationService.deleteIntegration(projectId)
      toast.success('Integration disconnected')
      setSelectedProvider('')
      setConfigForm({})
      setExternalProjectKey('')
      setStatusMappingForm({})
      queryClient.invalidateQueries(['project-integration', projectId])
      queryClient.invalidateQueries(['project-integration-statuses', projectId])
      queryClient.invalidateQueries(['project-integration-mappings', projectId])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to disconnect')
    }
  }

  const handleSaveMappings = async () => {
    const mappings = Object.entries(statusMappingForm)
      .filter(([_, val]) => val.external_status_id)
      .map(([statusId, val]) => ({
        project_status_id: parseInt(statusId),
        external_status_id: val.external_status_id,
        external_status_name: val.external_status_name,
      }))

    setSavingMappings(true)
    try {
      await integrationService.saveStatusMappings(projectId, mappings)
      toast.success('Status mappings saved')
      queryClient.invalidateQueries(['project-integration-mappings', projectId])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save mappings')
    } finally {
      setSavingMappings(false)
    }
  }

  const handleSync = async () => {
    setSyncing(true)
    try {
      const result = await integrationService.triggerSync(projectId)
      toast.success(result.message || 'Sync complete')
      queryClient.invalidateQueries(['project-integration', projectId])
      queryClient.invalidateQueries(['projects', projectId, 'tasks'])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Sync failed')
    } finally {
      setSyncing(false)
    }
  }

  const copyWebhookUrl = () => {
    if (integration?.webhook_url) {
      navigator.clipboard.writeText(integration.webhook_url)
      setWebhookCopied(true)
      setTimeout(() => setWebhookCopied(false), 2000)
      toast.success('Webhook URL copied')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Connection Form */}
      <div className="space-y-4">
        {!integration ? (
          <>
            {/* Provider Selection */}
            <div className="space-y-1.5">
              <Label>Provider</Label>
              <Select value={selectedProvider} onValueChange={(v) => {
                setSelectedProvider(v)
                setConfigForm({})
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a provider..." />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p.name} value={p.name}>{p.displayName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dynamic Config Fields */}
            {currentProvider?.configFields.map((field) => (
              <div key={field.key} className="space-y-1.5">
                <Label htmlFor={`config-${field.key}`}>
                  {field.label} {field.required && '*'}
                </Label>
                <Input
                  id={`config-${field.key}`}
                  type={field.type === 'password' ? 'password' : 'text'}
                  placeholder={field.placeholder}
                  value={configForm[field.key] || ''}
                  onChange={(e) => setConfigForm(f => ({ ...f, [field.key]: e.target.value }))}
                />
              </div>
            ))}

            {/* External Project Key */}
            {selectedProvider && (
              <div className="space-y-1.5">
                <Label htmlFor="ext-key">External Project Key *</Label>
                <Input
                  id="ext-key"
                  placeholder="e.g. ACME"
                  value={externalProjectKey}
                  onChange={(e) => setExternalProjectKey(e.target.value.toUpperCase())}
                  className="uppercase font-mono w-40"
                />
                <p className="text-xs text-muted-foreground">
                  The project key in {currentProvider?.displayName || 'the external system'}
                </p>
              </div>
            )}

            {selectedProvider && (
              <Button onClick={handleConnect} disabled={saving}>
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting...</>
                ) : (
                  <><Link2 className="h-4 w-4 mr-2" />Connect</>
                )}
              </Button>
            )}
          </>
        ) : (
          <>
            {/* Connected State */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
              <div className="h-2 w-2 rounded-full bg-green-500" />
              <div className="flex-1">
                <p className="text-sm font-medium">
                  Connected to {currentProvider?.displayName || integration.provider}
                </p>
                <p className="text-xs text-muted-foreground">
                  Project: {integration.external_project_key}
                  {integration.last_synced_at && (
                    <> &middot; Last synced: {new Date(integration.last_synced_at).toLocaleString()}</>
                  )}
                </p>
              </div>
              <Badge variant={integration.sync_enabled ? 'default' : 'secondary'}>
                {integration.sync_enabled ? 'Sync On' : 'Sync Off'}
              </Badge>
            </div>

            {/* Update Config */}
            <div className="space-y-3">
              {currentProvider?.configFields.map((field) => (
                <div key={field.key} className="space-y-1.5">
                  <Label htmlFor={`config-${field.key}`}>{field.label}</Label>
                  <Input
                    id={`config-${field.key}`}
                    type={field.type === 'password' ? 'password' : 'text'}
                    placeholder={field.placeholder}
                    value={configForm[field.key] || ''}
                    onChange={(e) => setConfigForm(f => ({ ...f, [field.key]: e.target.value }))}
                  />
                </div>
              ))}

              <div className="space-y-1.5">
                <Label htmlFor="ext-key">External Project Key</Label>
                <Input
                  id="ext-key"
                  value={externalProjectKey}
                  onChange={(e) => setExternalProjectKey(e.target.value.toUpperCase())}
                  className="uppercase font-mono w-40"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={syncEnabled}
                  onCheckedChange={setSyncEnabled}
                  id="sync-toggle"
                />
                <Label htmlFor="sync-toggle" className="text-sm">Enable two-way sync</Label>
              </div>

              <Button onClick={handleConnect} disabled={saving} size="sm">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>

            <Separator />

            {/* Webhook URL */}
            {integration.webhook_url && (
              <div className="space-y-2">
                <Label>Webhook URL</Label>
                <p className="text-xs text-muted-foreground">
                  Register this URL in your {currentProvider?.displayName || integration.provider} project for automatic inbound sync.
                </p>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={integration.webhook_url}
                    className="text-xs font-mono flex-1"
                  />
                  <Button variant="outline" size="sm" onClick={copyWebhookUrl}>
                    {webhookCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}

            <Separator />

            {/* Status Mapping */}
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold">Status Mapping</h3>
                <p className="text-xs text-muted-foreground">
                  Map your project statuses to {currentProvider?.displayName || integration.provider} statuses for accurate sync.
                </p>
              </div>

              {localStatuses.length === 0 ? (
                <p className="text-sm text-muted-foreground">No local statuses found.</p>
              ) : (
                <div className="space-y-2">
                  {localStatuses.map((status) => (
                    <div key={status.id} className="flex items-center gap-3">
                      <div className="w-40">
                        <span className="text-sm">{status.name}</span>
                        <span className="text-xs text-muted-foreground ml-1">({status.category})</span>
                      </div>
                      <span className="text-muted-foreground text-xs">&rarr;</span>
                      <Select
                        value={statusMappingForm[status.id]?.external_status_id || '_none'}
                        onValueChange={(val) => {
                          if (val === '_none') {
                            setStatusMappingForm(f => ({
                              ...f,
                              [status.id]: { external_status_id: '', external_status_name: '' }
                            }))
                            return
                          }
                          const extStatus = externalStatuses.find(s => s.id === val)
                          setStatusMappingForm(f => ({
                            ...f,
                            [status.id]: {
                              external_status_id: val,
                              external_status_name: extStatus?.name || '',
                            }
                          }))
                        }}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select status..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">-- None --</SelectItem>
                          {externalStatuses.map((es) => (
                            <SelectItem key={es.id} value={es.id}>{es.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}

              {externalStatuses.length === 0 && localStatuses.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-amber-600">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>External statuses not loaded. Save the integration first.</span>
                </div>
              )}

              <Button onClick={handleSaveMappings} disabled={savingMappings} size="sm" variant="outline">
                {savingMappings ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Save Mappings
              </Button>
            </div>

            <Separator />

            {/* Sync Controls */}
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold">Manual Sync</h3>
                <p className="text-xs text-muted-foreground">
                  Import all existing issues from {currentProvider?.displayName || integration.provider}. New issues will be created, existing ones updated.
                </p>
              </div>
              <Button onClick={handleSync} disabled={syncing} size="sm" variant="outline">
                {syncing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Syncing...</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" />Sync Now</>
                )}
              </Button>
            </div>

            <Separator />

            {/* Disconnect */}
            <div className="border border-destructive/30 rounded-lg p-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Disconnect Integration</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Remove the connection. Existing synced tasks will keep their external links but won't sync anymore.
                </p>
              </div>
              <Button variant="destructive" size="sm" onClick={() => setDisconnectOpen(true)}>
                <Unlink className="h-4 w-4 mr-1.5" />
                Disconnect
              </Button>
            </div>
          </>
        )}
      </div>

      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect integration?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the integration and status mappings. Existing synced tasks will keep their external links but two-way sync will stop.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default IntegrationSettings
